/**
 * SequencingCheatsheetPage — iter150AG Part 9 (P2 improvement).
 *
 * Single-page printable operator reference. Mirrors
 * /app/docs/SEQUENCING_CHEATSHEET.md verbatim so shop-floor crews can
 * Cmd-P and walk away with a laminated A4 sheet.
 *
 * Print styles:
 *   - Hide all UI chrome (header, back button, print button itself).
 *   - Force black-on-white, compact line-height, no shadows.
 *   - Use CSS columns so the densest sections fit on one A4 page.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";


export default function SequencingCheatsheetPage() {
  return (
    <div className="min-h-screen bg-slate-100 cheatsheet-root" data-testid="seq-cheatsheet">
      <style>{`
        @media print {
          .cheatsheet-no-print { display: none !important; }
          .cheatsheet-root { background: #fff !important; }
          .cheatsheet-sheet {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
          }
          body { font-size: 9pt; }
          h1 { font-size: 14pt; margin: 0 0 4pt 0; }
          h2 { font-size: 11pt; margin: 6pt 0 3pt 0; page-break-after: avoid; }
          h3 { font-size: 10pt; margin: 4pt 0 2pt 0; }
          p, li, td, th { line-height: 1.25; }
          table { page-break-inside: avoid; font-size: 8pt; }
          pre { font-size: 8pt; line-height: 1.2; page-break-inside: avoid; }
          a { color: #000 !important; text-decoration: none !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>

      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3 cheatsheet-no-print">
        <Link to="/sequencing" data-testid="seq-cheatsheet-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            Sequencing operator cheat-sheet
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            Print and stick next to the compressor-room PC.
          </p>
        </div>
        <button
          type="button"
          data-testid="seq-cheatsheet-print"
          onClick={() => window.print()}
          className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5">
          <Printer size={14} weight="bold" /> Print
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <article className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 md:p-8 cheatsheet-sheet text-slate-800 leading-relaxed">
          <h1 className="text-2xl font-bold font-['Chivo'] mb-1">Sequencing Cheat Sheet</h1>
          <p className="text-xs text-slate-500 mb-4 italic">
            Shop-floor edition — iter150AG · Feb 2026. For full reference see <code>docs/SEQUENCING_MANUAL.md</code>.
          </p>
          <hr className="my-4" />

          <Section title="FSM — the 13 forward steps (must walk in order)">
            <pre className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[11px] font-mono leading-snug whitespace-pre-wrap">
{`1.  idle                     →  click Advance to start
2.  detecting                →  CP fetches device list
3.  parameter_registration   →  set control_mode per member
4.  type_verified            →  pin each member to a Type catalog row
5.  pset_set                 →  enter common Pset (bar)
6.  disclaimer1_accepted     →  sign PRE-TEST disclaimer
7.  empty_system_prompt      →  confirm lines emptied
8.  fill_test                →  one fill test per member
9.  loss_test                →  ≥1 loss test (auto leak-rate)
10. disclaimer2_accepted     →  sign AUTO-CONTROL disclaimer
11. schedule_set             →  ≥1 weekday slot run=true
12. ready_to_start           →  readiness card must be green
13. auto_sequencing          →  ENGINE FIRES COMMANDS ✱`}
            </pre>
            <p className="mt-2 text-xs">
              <b>Side states (always reachable):</b><br />
              <code>paused</code> — operator hold · engine OFF · resume via ready_to_start.<br />
              <code>fault</code> — terminal until <b>Reset</b> · engine OFF.
            </p>
            <p className="mt-1 text-xs">
              Strictest guard: entry into <code>auto_sequencing</code> rejects any member in <code>local</code> mode or <code>fault</code>.
            </p>
          </Section>

          <Section title="Engine — what happens every 10 seconds">
            <p className="text-xs mb-2">
              Pressure band = <code>[Pset − bw/2, Pset + bw/2]</code>
            </p>
            <Table cols={["Situation", "Action", "reason_code"]} rows={[
              ["Pressure inside band", "HOLD", "pressure_in_band"],
              ["Pressure < lower band, idle ready", "START lowest-h", "pressure_below_band"],
              ["Below band, restart-delay holding", "HOLD", "restart_delay_in_effect"],
              ["Pressure > upper band, running available", "STOP highest-h", "pressure_above_band"],
              ["Above band, stop-delay holding", "HOLD", "stop_delay_in_effect"],
              ["Schedule slot closed", "SHED ALL", "schedule_closed"],
              ["Holiday with run=false", "SHED ALL", "holiday_no_run"],
              ["All members in local mode", "HOLD", "all_local"],
              ["No pressure sample", "HOLD (safe)", "no_pressure_sample"],
              ["In-band, VSD master running", "set_setpoint Hz", "pressure_in_band"],
            ]} />
            <p className="mt-2 text-xs">
              <b>Rotation policies (per group):</b><br />
              <code>lowest_hours</code> · default — lowest h starts first.<br />
              <code>daily</code> · lead rotates every calendar day.<br />
              <code>hour_delta</code> · promote lowest-h once spread ≥ threshold (default 50 h).<br />
              Commands fire <b>only</b> when <code>state == auto_sequencing</code> AND <code>engine_enabled == True</code>.
            </p>
          </Section>

          <Section title="VSD continuous setpoint control">
            <p className="text-xs">
              For members flagged <code>is_vsd_master</code> on the Type catalog, the engine emits a continuous frequency setpoint (clamped to <code>[vsd_min_hz, vsd_max_hz]</code>) every tick while pressure is in band — instead of binary start/stop. Formula:
            </p>
            <pre className="bg-slate-50 border border-slate-200 rounded-sm p-2 text-[11px] font-mono mt-1">
{`offset  = Pset − pressure
g       = clamp(offset / (bw/2), −1, +1)
target  = mid + g · (max − min) / 2`}
            </pre>
            <p className="text-xs mt-1">CP command: <code>submit_command(set_setpoint, {`{setpoint_hz, min_hz, max_hz}`})</code>.</p>
          </Section>

          <Section title="Color codes on the live dashboard">
            <Table cols={["Chip", "Meaning"]} rows={[
              ["IN BAND (emerald)", "Pressure inside [low, high] — engine holds."],
              ["ABOVE BAND (rose)", "Too high — engine wants to stop someone."],
              ["BELOW BAND (rose)", "Too low — engine wants to start someone."],
              ["IN WORKING HOURS (emerald)", "Today's schedule slot is open now."],
              ["OUTSIDE HRS (slate)", "Schedule closed → engine sheds."],
              ["HOLIDAY (amber)", "Today is a run=false holiday → engine sheds."],
              ["cmd ✓ (emerald)", "Last decision actually issued a CP command."],
              ["CP: LIVE (emerald)", "Real Control Plane."],
              ["CP: MOCK (amber)", "In-process mock (no real I/O)."],
              ["FAULT BANNER (rose)", "≥1 open fault alert — emails dispatched to operators + admins."],
            ]} />
          </Section>

          <Section title="Voltage & Amps photo rules (commissioning + warranty)">
            <p className="text-xs font-bold mb-1">Manual typing is NEVER allowed — every reading needs a photo first.</p>
            <h3 className="text-xs font-bold mt-2">Single-phase (200–240V)</h3>
            <ul className="text-xs list-disc pl-5 space-y-0.5">
              <li>2 voltage photos: 1 <b>running</b> + 1 <b>stopped</b>.</li>
              <li>1 amps photo: <b>running</b> only.</li>
            </ul>
            <h3 className="text-xs font-bold mt-2">3-phase</h3>
            <ul className="text-xs list-disc pl-5 space-y-0.5">
              <li>6 voltage photos: 3 phase-pairs (L1↔L2, L2↔L3, L1↔L3) × 2 states (running + stopped).</li>
              <li>3 amps photos: L1 + L2 + L3, <b>running only</b>.</li>
            </ul>
            <Table cols={["Border colour", "Meaning"]} rows={[
              ["Slate", "No photo yet."],
              ["Emerald", "AI read it · confidence ≥70%."],
              ["Rose", "AI couldn't read · retake or override."],
              ["Amber", "Manual override active (AI value preserved for audit)."],
            ]} />
            <p className="text-xs mt-1 italic">Warranty: voltage required, amps optional (only if compressor still runs).</p>
          </Section>

          <Section title="Emergency procedures">
            <Table cols={["Situation", "Do this"]} rows={[
              ["Sequencer doing something dangerous", "Commissioning page → Pause (engine OFF, state retained)"],
              ["Compressor is faulted", "Commissioning page → Mark fault → fix → Reset"],
              ["Lost confidence in CP credentials", "Super-admin → click CP chip → Test connection"],
              ["Need to redo a member's commissioning", "Commissioning page → Reset → walk FSM again"],
              ["Group should never start (off-season)", "Engine tab → Disable"],
              ["Operator missed fault email", "Live dashboard fault banner shows the most recent open alerts"],
            ]} />
          </Section>

          <Section title="Where things live">
            <Table cols={["Page", "URL"]} rows={[
              ["Sequencing home", "/sequencing"],
              ["Build a new group", "/sequencing/groups/new"],
              ["All groups", "/sequencing/groups"],
              ["Group detail (4 tabs)", "/sequencing/groups/:gid"],
              ["Commissioning wizard", "/sequencing/groups/:gid/commissioning"],
              ["Live dashboard", "/sequencing/groups/:gid/live"],
              ["Types catalog (admin)", "/sequencing/types"],
              ["Disclaimers (admin)", "/sequencing/disclaimers"],
              ["Holidays (admin)", "/sequencing/holidays"],
              ["Device templates (admin)", "/sequencing/admin/device-templates"],
              ["This cheat-sheet", "/sequencing/cheatsheet"],
              ["CP credentials modal (super-admin)", "Click CP: MOCK/LIVE chip on /sequencing"],
            ]} />
          </Section>

          <Section title="DON'Ts">
            <ul className="text-xs list-disc pl-5 space-y-1">
              <li>Don't enable the engine on a group that hasn't reached <code>auto_sequencing</code> (the FSM will block you — don't try to bypass via Mongo).</li>
              <li>Don't call <code>POST /api/sequencing/admin/control-plane/reset</code> ad-hoc — it wipes live CP credentials.</li>
              <li>Don't type voltage/amps numbers — the platform forces photos for audit. Use <b>Override</b> only when a photo is captured AND the AI got it wrong.</li>
              <li>Don't span tenants in one group — the picker will lock to the first compressor's tenant; respect the banner.</li>
            </ul>
          </Section>

          <hr className="my-4" />
          <p className="text-[10px] text-slate-400 text-center italic">
            iter150AG · Feb 2026 · Full reference: <code>docs/SEQUENCING_MANUAL.md</code> · Walkthrough: <code>docs/SEQUENCING_TUTORIAL.md</code>
          </p>
        </article>
      </main>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <section className="mt-5">
      <h2 className="text-base font-bold font-['Chivo'] text-slate-900 mb-1.5 pb-1 border-b border-slate-200">
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}


function Table({ cols, rows }) {
  return (
    <table className="w-full text-xs border-collapse my-2">
      <thead>
        <tr className="bg-slate-100">
          {cols.map((c, i) => (
            <th key={i} className="text-left border border-slate-300 px-2 py-1 font-bold text-slate-700">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td key={j} className="border border-slate-200 px-2 py-1 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
